import { StrainType } from '../../../../../../../../../models/enum/dto/strain-classification.enum';
import { CannabisUnitOfMeasure } from '../../../../../../../../../models/enum/dto/cannabis-unit-of-measure.enum';
import { SectionItemOverflowStatus } from '../../../../../../../../../models/enum/shared/section-item-overflow-status.enum';
import { CannabinoidDisplayType } from '../../../../../../../../../models/enum/shared/cannabinoid-display-type.enum';
import { VariantType } from '../../../../../../../../../models/enum/dto/variant-type.enum';
import { ProductType } from '../../../../../../../../../models/enum/dto/product-type.enum';
import { SectionLayoutType } from '../../../../../../../../../models/enum/dto/section-layout-type.enum';
import { StringUtils } from '../../../../../../../../../utils/string-utils';
import { LabelComponentInterface } from '../../../../../../../../labels/components/label-component-interface';
import { ArrayUtils } from '../../../../../../../../../utils/array-utils';
import { StrainTypeUtils } from '../../../../../../../../../utils/strain-type-utils';
import { LocationPriceStream } from '../../../../../../../../../models/enum/shared/location-price-stream';
import { exists } from '../../../../../../../../../functions/exists';
import { VariantTypeUtils } from '../../../../../../../../../utils/variant-type-utils';
import { MenuUtils } from '../../../../../../../../../utils/menu-utils';
import type { CompanyConfiguration } from '../../../../../../../../../models/company/dto/company-configuration';
import type { LocationConfiguration } from '../../../../../../../../../models/company/dto/location-configuration';
import type { Menu } from '../../../../../../../../../models/menu/menu';
import type { MenuStyle } from '../../../../../../../../../models/menu/dto/menu-style';
import type { Product } from '../../../../../../../../../models/product/dto/product';
import type { ProductMenu } from '../../../../../../../../../models/menu/product-menu';
import { SectionColumnViewModel } from '../section-column-view-models/SectionColumnViewModel';
import type { SectionWithProducts } from '../../../../../../../../../models/menu/section/section-with-products';
import type { Subheader } from '../../../../../../../../../models/menu/section/subheader';
import type { Variant } from '../../../../../../../../../models/product/dto/variant';
import type { VariantBadge } from '../../../../../../../../../models/product/dto/variant-badge';
import { BadgeUtils } from '../../../../../../../../../utils/badge-utils';
import { TerpeneUnitOfMeasure } from '../../../../../../../../../models/enum/dto/terpene-unit-of-measure';
import { TerpeneDisplayType } from '../../../../../../../../../models/enum/shared/terpene-display-type';
import { SortUtils } from '../../../../../../../../../utils/sort-utils';
import { NumberUtils } from '../../../../../../../../../utils/number.utils';
import { memoize } from '../../../../../../../../shared/decorators/memoize';
import { SectionColumnConfigProductInfoKey } from '../../../../../../../../../models/enum/shared/section-column-config-product-info-key';
import {
  SectionColumnConfigDataBadgeMode,
  SectionColumnConfigDataForCannabinoidsAndTerpenesColumn,
  SectionColumnConfigDataForSecondaryPriceColumn,
  SectionColumnConfigDataForStrainTypeColumn,
  SectionColumnConfigDataValue
} from '../../../../../../../../../models/enum/shared/section-column-config-data-value';
import { SectionColumnConfigCollectiveTerpeneKey } from '../../../../../../../../../models/enum/shared/section-column-config-collective-terpene-key';
import { GradingProvider } from '../../../../../../../../../models/enum/shared/grading-provider';
import * as uuid from 'uuid';
import { Cannabinoid, PrimaryCannabinoid } from '../../../../../../../../../models/enum/shared/cannabinoid';
import { SectionColumnProductInfoType } from 'src/app/models/enum/shared/section-column-product-info-type';
import { BadgeMapping } from 'src/app/models/menu/dto/badge-mapping';
import { BadgeAsset } from 'src/app/models/image/dto/badge-asset';
import { SectionColumnType } from 'src/app/models/enum/shared/section-column-type';
import { SectionColumnTerpeneType } from '../../../../../../../../../models/enum/shared/section-column-terpene-type';
import { SectionColumnCannabinoidType } from '../../../../../../../../../models/enum/shared/section-column-cannabinoid-type';
import { SectionColumnConfigCollectiveCannabinoidKey } from 'src/app/models/enum/shared/section-column-config-collective-cannabinoid-key';
import { PrintCardThemeId } from '../../../../../../../../../models/enum/dto/theme.enum';
import { RegexUtils } from 'src/app/utils/regex-utils';
import { environment } from '../../../../../../../../../../environments/environment';
import type { ProductTag } from '../../../../../../../../../models/product/dto/product-tag';
import { CompoundUtils } from '../../../../../../../../../utils/compound-utils';

type ColumnListItem = {
  name: string;
  value: string;
  isPresent: boolean;
};

export class SectionRowViewModel implements LabelComponentInterface {
  id: string = uuid.v4();
  menu: ProductMenu;
  section: SectionWithProducts;
  subHeader: Subheader;
  product: Product;
  rowVariants: Variant[];
  variantLineItemMode = false;
  collapseContent = false;
  hideLabel = false;
  configTheme: string;
  style: MenuStyle;
  hidePriceOnVariantIds: string[] = [];
  companyConfig: CompanyConfiguration;
  locationConfig: LocationConfiguration;
  variantBadgeMap: Map<string, VariantBadge[]> = new Map<string, VariantBadge[]>();
  itemOverflowStatus: SectionItemOverflowStatus = SectionItemOverflowStatus.NotSet;
  // used for section level snap transition
  private uniqueIdOffset = 0;

  @memoize
  getBrand(): string {
    return this.product?.getBrand(this.rowVariants?.map(v => v.id));
  }

  getBrandFor(variant: Variant): string {
    return variant?.brand;
  }

  @memoize
  getManufacturer(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.manufacturer));
  }

  getProductSubtitle(): string {
    return this.menu?.getProductSubtitle(this.section, this.product, this);
  }

  getProductTertiaryTitle(): string {
    return this.menu?.getProductTertiaryTitle(this.section, this.product, this);
  }

  getImageContainerFit(): string {
    const config = this.section?.columnConfig?.get(SectionColumnConfigProductInfoKey.Asset);
    return config?.dataValue ?? 'contain';
  }

  getImageOpacity(): number {
    const config = this.section?.columnConfig?.get(SectionColumnConfigProductInfoKey.Asset);
    const opacity = config?.columnOpacity;
    return opacity === 0 ? 1 : opacity;
  }

  @memoize
  getProductTagNamesWithinTagGroup(
    enabledCuratedProductTagGroupIds: string[],
    enabledCuratedProductTagIds: string[],
    productTagGroupColumnType: string,
    variant?: Variant | undefined,
    returnIdInstead?: boolean
  ): string[] {
    const tagGroup = this.menu?.getProductTagGroup(this.section, productTagGroupColumnType);
    const relevantTags = tagGroup?.getRelevantTagsFor(
      enabledCuratedProductTagGroupIds,
      enabledCuratedProductTagIds,
      exists(variant) ? [variant] : this.getScopedVisibleVariants()
    );
    if (returnIdInstead) return relevantTags?.sort(SortUtils.sortTagsByName).map(tag => tag.id);
    return relevantTags?.map(tag => tag?.name)?.sort(SortUtils.stringAscending);
  }

  @memoize
  getRowTitle(): string {
    return this.menu?.getProductTitle(this.section, this.product, this.rowVariants, this.locationConfig?.priceFormat);
  }

  @memoize
  getRowTitleFor(variant: Variant): string {
    return this.menu?.getProductTitle(this.section, this.product, [variant], this.locationConfig?.priceFormat);
  }

  @memoize
  getRowTitleForFirstRowVariant(): string {
    return this.getRowTitleFor(this.rowVariants?.firstOrNull());
  }

  @memoize
  getVariantIdsSeperatedBy(separator: string): string {
    return this.rowVariants?.map(v => v.id).join(separator);
  }

  @memoize
  getLineage(): string | null {
    return (this.variantLineItemMode ? this.rowLineage() : this.product?.getLineage()) ?? null;
  }

  @memoize
  getStrain(): string | null {
    return (this.variantLineItemMode ? this.rowStrain() : this.product?.getStrain()) ?? null;
  }

  /**
   * This is different from rowStrainType(), because it uses all the variants attached to the product to calculate
   * the strain type.
   *
   * rowStrainType() only uses the variants associated with this row item.
   *
   * Use getReadableStrainType if you need to display the strain type in the UI.
   */
  @memoize
  getStrainType(): StrainType {
    return (this.variantLineItemMode ? this.rowStrainType() : this.product?.classification) ?? StrainType.UNKNOWN;
  }

  /**
   * This gets the strain type based on ALL the variants associated with a product, and not just the variants
   * associated with the line item.
   */
  @memoize
  getReadableStrainType(
    strainTypeMode: SectionColumnConfigDataValue = SectionColumnConfigDataForStrainTypeColumn.StrainTypeWord
  ): string {
    const strainType = this.getStrainType();
    if (strainTypeMode === SectionColumnConfigDataForStrainTypeColumn.StrainTypeSymbol) {
      return StrainTypeUtils.getSymbol(strainType);
    }
    return StrainTypeUtils.getStandardizedName(strainType, this.menu?.shouldStandardizeDominantStrainType());
  }

  @memoize
  getStrainTypeDataToClass(strainTypeMode: SectionColumnConfigDataValue): string {
    return StringUtils.pascalCaseToKebabCase(strainTypeMode);
  }

  @memoize
  getStrainTypeBadge(strainTypeMode: SectionColumnConfigDataValue): BadgeAsset {
    if (strainTypeMode === SectionColumnConfigDataBadgeMode.Badge) {
      return this.getBadgeAssetForColumnType(SectionColumnProductInfoType.StrainType)?.image;
    }
  }

  @memoize
  getReadableStrainTypeFor(
    variant: Variant,
    strainTypeMode: SectionColumnConfigDataValue = SectionColumnConfigDataForStrainTypeColumn.StrainTypeWord
  ): string {
    const strainType = variant?.classification;
    if (strainTypeMode === SectionColumnConfigDataForStrainTypeColumn.StrainTypeSymbol) {
      return StrainTypeUtils.getSymbol(strainType);
    } else {
      return StrainTypeUtils.getStandardizedName(strainType, this.menu?.shouldStandardizeDominantStrainType());
    }
  }

  @memoize
  getReadableStrainTypeForFirstRowVariant(
    strainTypeMode: SectionColumnConfigDataValue = SectionColumnConfigDataForStrainTypeColumn.StrainTypeWord
  ): string {
    return this.getReadableStrainTypeFor(this.rowVariants?.firstOrNull(), strainTypeMode);
  }

  @memoize
  getStrainTypeDataValue(): SectionColumnConfigDataValue {
    return this.section?.columnConfig?.get(SectionColumnConfigProductInfoKey.StrainType)?.dataValue;
  }

  @memoize
  getStrainTypeColor(): string {
    switch (this.getStrainType()) {
      case StrainType.Indica:
      case StrainType.IndicaDominant:
        return '#7640EF';
      case StrainType.Sativa:
      case StrainType.SativaDominant:
        return '#EF4040';
      case StrainType.Hybrid:
        return '#55C678';
      case StrainType.Blend:
        return '#6F6F6F';
      case StrainType.CBD:
        return '#4E6A9D';
      case StrainType.UNKNOWN:
        return '#222222';
      default:
        return '#222222';
    }
  }

  @memoize
  getMaxRowStock(): number {
    return Math.max(...this.getScopedVisibleVariantsElseRowVariants().map(v => v.inventory.quantityInStock));
  }

  @memoize
  getMinRowPrice(): number {
    const themeId = this.menu?.theme;
    const rowVariants = this.getScopedVisibleVariantsElseRowVariants() || [];
    const locationId = this.menu?.locationId;
    const companyId = this.menu?.companyId;
    const priceStream = this.locationConfig?.priceFormat;
    const hideSale = this.getHideSale();
    return Math.min(...rowVariants.map(v => v.getVisiblePrice(themeId, locationId, companyId, priceStream, hideSale)));
  }

  @memoize
  getMinRowSecondaryPrice(): number {
    const themeId = this.menu?.theme;
    const locId = this.menu?.locationId;
    const companyId = this.menu?.companyId;
    const priceStream = this.locationConfig?.priceFormat;
    const hideSale = this.getHideSale();
    const mode = this.section?.columnConfig?.get(SectionColumnConfigProductInfoKey.SecondaryPrice)?.dataValue;
    const rowVariants = this.getScopedVisibleVariantsElseRowVariants() || [];
    switch (mode) {
      case SectionColumnConfigDataForSecondaryPriceColumn.PricePerUOM:
        return Math.min(...rowVariants.map(v => v.getPricePerUOM(themeId, locId, companyId, priceStream, hideSale)));
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalPrice:
        return Math.min(...rowVariants.map(v => v.getPriceWithoutDiscounts(themeId, locId, companyId, priceStream)));
      case SectionColumnConfigDataForSecondaryPriceColumn.SaleOriginalPrice:
        const saleOriginalPrices = rowVariants
          .map(v => v.getSaleOriginalPriceOrNull(themeId, locId, companyId, priceStream))
          .filterNulls();
        return saleOriginalPrices.length > 0 ? Math.min(...saleOriginalPrices) : null;
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInPrice:
        return Math.min(...rowVariants.map(v => v.getTaxesInPrice(themeId, locId, companyId)));
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInRoundedPrice:
        return Math.min(...rowVariants.map(v => v.getTaxesInRoundedPrice(themeId, locId, companyId)));
      case SectionColumnConfigDataForSecondaryPriceColumn.PreTaxPrice:
        return Math.min(...rowVariants.map(v => v.getPreTaxPrice(themeId, locId, companyId)));
      default:
        return Math.min(...rowVariants.map(v => v.getSecondaryPrice(themeId, companyId, locId, priceStream)));
    }
  }

  @memoize
  getMaxRowPrice(): number {
    const themeId = this.menu?.theme;
    const locationId = this.menu?.locationId;
    const companyId = this.menu?.companyId;
    const priceStream = this.locationConfig?.priceFormat;
    const hideSale = this.getHideSale();
    const rowVariants = this.getScopedVisibleVariantsElseRowVariants() || [];
    return Math.max(...rowVariants.map(v => v.getVisiblePrice(themeId, locationId, companyId, priceStream, hideSale)));
  }

  @memoize
  getMaxRowSecondaryPrice(): number {
    const themeId = this.menu?.theme;
    const locId = this.menu?.locationId;
    const companyId = this.menu?.companyId;
    const priceStream = this.locationConfig?.priceFormat;
    const hideSale = this.getHideSale();
    const mode = this.section?.columnConfig?.get(SectionColumnConfigProductInfoKey.SecondaryPrice)?.dataValue;
    const rowVariants = this.getScopedVisibleVariantsElseRowVariants() || [];
    switch (mode) {
      case SectionColumnConfigDataForSecondaryPriceColumn.PricePerUOM:
        return Math.max(...rowVariants.map(v => v.getPricePerUOM(themeId, locId, companyId, priceStream, hideSale)));
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalPrice:
        return Math.max(...rowVariants.map(v => v.getPriceWithoutDiscounts(themeId, locId, companyId, priceStream)));
      case SectionColumnConfigDataForSecondaryPriceColumn.SaleOriginalPrice:
        const saleOriginalPrices = rowVariants
          .map(v => v.getSaleOriginalPriceOrNull(themeId, locId, companyId, priceStream))
          .filterNulls();
        return saleOriginalPrices.length > 0 ? Math.max(...saleOriginalPrices) : null;
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInPrice:
        return Math.max(...rowVariants.map(v => v.getTaxesInPrice(themeId, locId, companyId)));
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInRoundedPrice:
        return Math.max(...rowVariants.map(v => v.getTaxesInRoundedPrice(themeId, locId, companyId)));
      case SectionColumnConfigDataForSecondaryPriceColumn.PreTaxPrice:
        return Math.max(...rowVariants.map(v => v.getPreTaxPrice(themeId, locId, companyId)));
      default:
        return Math.max(...rowVariants.map(v => v.getSecondaryPrice(themeId, companyId, locId, priceStream)));
    }
  }

  /**
   * Additional Details
   */
  @memoize
  getEffects(): string | null {
    return (this.variantLineItemMode ? this.rowEffects() : this.product?.getEffects()) ?? null;
  }

  @memoize
  getAromas(): string | null {
    return (this.variantLineItemMode ? this.rowAromas() : this.product?.getAromas()) ?? null;
  }

  @memoize
  getFlavorGroup(): string | null {
    return (this.variantLineItemMode ? this.rowFlavorGroup() : this.product?.getFlavorGroup()) ?? null;
  }

  @memoize
  getFlavors(): string | null {
    return (this.variantLineItemMode ? this.rowFlavors() : this.product?.getFlavors()) ?? null;
  }

  @memoize
  getHelpsWith(): string | null {
    return (this.variantLineItemMode ? this.rowHelpsWith() : this.product?.getHelpsWith()) ?? null;
  }

  @memoize
  getAppearances(): string | null {
    return (this.variantLineItemMode ? this.rowAppearances() : this.product?.getAppearances()) ?? null;
  }

  @memoize
  getGrowingConditions(): string | null {
    return (this.variantLineItemMode ? this.rowGrowingConditions() : this.product?.getGrowingConditions()) ?? null;
  }

  @memoize
  getCraftDesignations(): string | null {
    return (this.variantLineItemMode ? this.rowCraftDesignations() : this.product?.getCraftDesignations()) ?? null;
  }
  /**
   * -1 is used internally to represent a missing value or invalid value for min/max calculations.
   *
   * We filter out negative values from the min/max calculations so the following example works:
   *
   *  a) CBD - min: 3%, max: 7%
   *  b) CBD - min: 1%, max: 6%
   *  c) CBD - min: empty, max: empty
   *
   *  Outcome would be min: 1%, max: 7%. If we didn't filter out -1, the outcome would be '-'
   */
  @memoize
  protected getRowRangeCannabinoid(
    cannabinoid: string,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    let minVal: number;
    let maxVal: number;
    const variants = variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants();
    if (this.companyConfig?.cannabinoidDisplayType === CannabinoidDisplayType.Exact) {
      const [rangeVariants, exactVariants] = ArrayUtils.partition(variants, v => v?.useCannabinoidRange ?? false);
      const exactValues = exactVariants?.map(v => v?.getNumericCannabinoidOrTerpene(cannabinoid)) ?? [];
      const rangeMinValues = rangeVariants?.map(v => v?.getNumericMinCannabinoidOrTerpene(cannabinoid)) ?? [];
      const rangeMaxValues = rangeVariants?.map(v => v?.getNumericMaxCannabinoidOrTerpene(cannabinoid)) ?? [];
      const minValues = [...exactValues, ...rangeMinValues].filter(v => v >= 0);
      const maxValues = [...exactValues, ...rangeMaxValues].filter(v => v >= 0);
      minVal = Math.min(...minValues);
      maxVal = Math.max(...maxValues);
    } else {
      const min = variants?.map(v => v?.getNumericMinCannabinoidOrTerpene(cannabinoid))?.filter(v => v >= 0) ?? [];
      const max = variants?.map(v => v?.getNumericMaxCannabinoidOrTerpene(cannabinoid))?.filter(v => v >= 0) ?? [];
      minVal = Math.min(...min);
      maxVal = Math.max(...max);
    }
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(minVal)) minVal = -1;
    if (!Number.isFinite(maxVal)) maxVal = -1;
    const thcOrCbd =
      cannabinoid === PrimaryCannabinoid.THC ||
      cannabinoid === PrimaryCannabinoid.CBD ||
      cannabinoid === PrimaryCannabinoid.THCAndCBD;
    const isListCannabinoid =
      displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.ListNumericValue;
    const showEntireNumber = !thcOrCbd || isListCannabinoid;
    return this.getFixedPointRangeStringNoUnits(minVal, maxVal, displayFormat, fixedPoint, showEntireNumber);
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMinRowCannabinoid(cannabinoid: string, variant?: Variant): number {
    const minValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v?.useCannabinoidRange
          ? v?.getNumericMinCannabinoidOrTerpene(cannabinoid)
          : v?.getNumericCannabinoidOrTerpene(cannabinoid);
      })
      .filter(v => v >= 0);
    let min = Math.min(...minValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(min)) min = -1;
    return min;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMinRowTerpene(terpeneCamelCased: string, variant?: Variant): number {
    const minValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v?.useTerpeneRange
          ? v?.getNumericMinCannabinoidOrTerpene(terpeneCamelCased)
          : v?.getNumericCannabinoidOrTerpene(terpeneCamelCased);
      })
      .filter(v => v >= 0);
    let min = Math.min(...minValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(min)) min = -1;
    return min;
  }

  /**
   * -1 is used internally to represent a missing value or invalid value for min/max calculations.
   *
   * We filter out negative values from the min/max calculations so the following example works:
   *
   *  a) Alpha Bisabolol - min: 3%, max: 7%
   *  b) Alpha Bisabolol - min: 1%, max: 6%
   *  c) Alpha Bisabolol - min: empty, max: empty
   *
   *  Outcome would be min: 1%, max: 7%. If we didn't filter out -1, the outcome would be '-'
   */
  @memoize
  protected getRowRangeTerpene(
    terpeneCamelCased: string,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    let minVal: number;
    let maxVal: number;
    const variants = variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants();
    if (this.companyConfig?.terpeneDisplayType === TerpeneDisplayType.Exact) {
      const [rangeVariants, exactVariants] = ArrayUtils.partition(variants, v => v?.useTerpeneRange ?? false);
      const exactValues = exactVariants?.map(v => v?.getNumericCannabinoidOrTerpene(terpeneCamelCased)) ?? [];
      const rangeMinValues = rangeVariants?.map(v => v?.getNumericMinCannabinoidOrTerpene(terpeneCamelCased)) ?? [];
      const rangeMaxValues = rangeVariants?.map(v => v?.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased)) ?? [];
      const minValues = [...exactValues, ...rangeMinValues].filter(v => v >= 0);
      const maxValues = [...exactValues, ...rangeMaxValues].filter(v => v >= 0);
      minVal = Math.min(...minValues);
      maxVal = Math.max(...maxValues);
    } else {
      const min = variants?.map(v => v?.getNumericMinCannabinoidOrTerpene(terpeneCamelCased))?.filter(v => v >= 0);
      const max = variants?.map(v => v?.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased))?.filter(v => v >= 0);
      minVal = Math.min(...(min || []));
      maxVal = Math.max(...(max || []));
    }
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(minVal)) minVal = -1;
    if (!Number.isFinite(maxVal)) maxVal = -1;
    return this.getFixedPointRangeStringNoUnits(minVal, maxVal, displayFormat, fixedPoint);
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMaxRowCannabinoid(cannabinoid: string): number {
    const maxValues = this.getScopedVisibleVariantsElseRowVariants()
      .map(v => {
        return v?.useCannabinoidRange
          ? v?.getNumericMaxCannabinoidOrTerpene(cannabinoid)
          : v?.getNumericCannabinoidOrTerpene(cannabinoid);
      })
      .filter(v => v >= 0);
    const max = Math.max(...maxValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(max)) return -1;
    return max;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMaxRowTerpene(terpeneCamelCased: string): number {
    const maxValues = this.getScopedVisibleVariantsElseRowVariants()
      .map(v => {
        return v.useTerpeneRange
          ? v.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased)
          : v.getNumericCannabinoidOrTerpene(terpeneCamelCased);
      })
      .filter(v => v >= 0);
    const max = Math.max(...maxValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(max)) return -1;
    return max;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMinRowTotalActiveCannabinoids(variant?: Variant): number {
    const minValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMinTotalActiveCannabinoids(this.companyConfig?.enabledSecondaryCannabinoids)
          : v.getNumericTotalActiveCannabinoids(this.companyConfig?.enabledSecondaryCannabinoids);
      })
      .filter(v => v >= 0);
    let min = Math.min(...minValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(min)) min = -1;
    return min;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMaxRowTotalActiveCannabinoids(variant?: Variant): number {
    const maxValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMaxTotalActiveCannabinoids(this.companyConfig?.enabledSecondaryCannabinoids)
          : v.getNumericTotalActiveCannabinoids(this.companyConfig?.enabledSecondaryCannabinoids);
      })
      .filter(v => v >= 0);
    let max = Math.max(...maxValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(max)) max = -1;
    return max;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMinRowTotalActiveTerpenes(variant?: Variant): number {
    const minValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v.useTerpeneRange
          ? v.getNumericMinTotalActiveTerpenes(this.companyConfig?.enabledTerpenes)
          : v.getNumericTotalActiveTerpenes(this.companyConfig?.enabledTerpenes);
      })
      .filter(v => v >= 0);
    let min = Math.min(...minValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(min)) min = -1;
    return min;
  }

  /**
   * -1 is used to represent a missing value or invalid value
   */
  @memoize
  getMaxRowTotalActiveTerpenes(variant?: Variant): number {
    const maxValues = (variant ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
      .map(v => {
        return v.useTerpeneRange
          ? v.getNumericMaxTotalActiveTerpenes(this.companyConfig?.enabledTerpenes)
          : v.getNumericTotalActiveTerpenes(this.companyConfig?.enabledTerpenes);
      })
      .filter(v => v >= 0);
    let max = Math.max(...maxValues);
    // convert infinity to -1 (-1 represents invalid or empty value)
    if (!Number.isFinite(max)) max = -1;
    return max;
  }

  @memoize
  getTopCannabinoid(): string {
    return (
      this.getScopedVisibleVariantsElseRowVariants()
        ?.map(v => {
          return v?.getTopCannabinoid(
            this.companyConfig?.enabledSecondaryCannabinoids ?? [],
            this.companyConfig?.ignoredListCannabinoids ?? []
          );
        })
        ?.filterNulls()
        ?.mode() ?? ''
    );
  }

  @memoize
  private getListCannabinoids(
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
    variant?: Variant
  ): string[] {
    return displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
      ? (this.getListCannabinoidsForNumericMode(variant) ?? [])
      : (this.getListCannabinoidsForPresenceModeAndBadgeMode(variant) ?? []);
  }

  private getIgnoredListCannabinoids(): Cannabinoid[] {
    return this.menu?.ignoredListCannabinoids?.emptyAsNull() ?? this.companyConfig?.ignoredListCannabinoids ?? [];
  }

  private getListCannabinoidsForNumericMode(variant?: Variant): string[] {
    const listNumeric = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.ListNumericValue;
    return this.getPresentCannabinoids(variant)
      ?.map(cannabinoid => {
        return {
          cannabinoid,
          // non-range rows display the row min, so the threshold has to be applied to the value that will be shown
          shownValue: this.displayCannabinoidInRanges()
            ? this.getMaxRowCannabinoid(cannabinoid)
            : this.getMinRowCannabinoid(cannabinoid),
          maxValue: this.getMaxRowCannabinoid(cannabinoid)
        };
      })
      ?.filter(metadata => metadata?.shownValue >= CompoundUtils.smallestAllowableValue)
      ?.sort((a, b) => SortUtils.numberDescending(a.maxValue, b.maxValue))
      ?.map(metadata => `${metadata.cannabinoid} ${this.getCannabinoid(metadata.cannabinoid, listNumeric)}`);
  }

  private getListCannabinoidsForPresenceModeAndBadgeMode(variant?: Variant): string[] {
    const pinnedMap = new Map<string, number>([[PrimaryCannabinoid.THC, 0]]);
    const presentCannabinoids = this.getPresentCannabinoids(variant);
    return presentCannabinoids.sort((a, b) => {
      return SortUtils.pinned(pinnedMap, a, b) || SortUtils.stringAscendingNullsLast(a, b);
    });
  }

  @memoize
  getTopTerpene(): string {
    return (
      this.getScopedVisibleVariantsElseRowVariants()
        ?.map(v => v?.getTopTerpene(this.companyConfig?.enabledTerpenes))
        ?.filterNulls()
        ?.mode() ?? ''
    );
  }

  @memoize
  getPresentTerpenes(): string[] {
    return (
      this.getScopedVisibleVariantsElseRowVariants()
        ?.flatMap(v => v?.getPresentTerpenes(this.companyConfig?.enabledTerpenes))
        ?.filterNulls()
        ?.unique() ?? []
    );
  }

  @memoize
  getPresentCannabinoids(variant?: Variant): Cannabinoid[] {
    return (
      (exists(variant) ? [variant] : this.getScopedVisibleVariantsElseRowVariants())
        ?.flatMap(variant => {
          return variant?.getPresentCannabinoids(
            this.companyConfig?.enabledSecondaryCannabinoids,
            this.getIgnoredListCannabinoids()
          );
        })
        ?.filterNulls()
        ?.unique() ?? []
    );
  }

  @memoize
  private getListTerpenes(
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
    variant?: Variant
  ): string[] {
    const variants = exists(variant) ? [variant] : this.getScopedVisibleVariantsElseRowVariants();
    const emptyState = this.getEmptyStateForTerpene(variants);
    if (emptyState !== null) return [];
    const enabledTerpenes = [...(this.companyConfig?.enabledTerpenes ?? [])];
    return displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
      ? this.getListTerpenesForNumericValues(variants, enabledTerpenes, displayFormat, variant)
      : this.getListTerpenesForPresentValues(variants, enabledTerpenes, variant);
  }

  private getListTerpenesForNumericValues(
    variants: Variant[],
    enabledTerpenes: string[],
    displayFormat: SectionColumnConfigDataValue,
    variant?: Variant
  ): string[] {
    const unitOfMeasureString = variants?.firstOrNull()?.getTerpeneUnitOfMeasureString(this.menu);
    return enabledTerpenes
      .map(terpene => {
        const value = this.displayTerpeneInRanges()
          ? this.getTerpeneRowRangeAsString(terpene, unitOfMeasureString, displayFormat, undefined, variant)
          : this.getTerpeneAsString(terpene, unitOfMeasureString, displayFormat, undefined, true, variant);
        return {
          name: StringUtils.splitPascalCasePreserveAcronyms(terpene),
          value: value,
          isPresent: value !== '-' && value !== '--' && value !== ''
        };
      })
      .filter(item => item.isPresent)
      .map(item => `${item.name} ${item.value}`);
  }

  private getListTerpenesForPresentValues(
    variants: Variant[],
    enabledTerpenes: string[],
    specificVariant?: Variant
  ): string[] {
    const uniqueTerps = new Set(
      exists(specificVariant)
        ? specificVariant.getPresentTerpenes(enabledTerpenes)
        : variants.flatMap(v => v.getPresentTerpenes(enabledTerpenes))
    );
    return [...uniqueTerps];
  }

  getListItemsForColumnType(
    columnType: SectionColumnType,
    displayFormat: SectionColumnConfigDataValue
  ): string[] | BadgeAsset[] {
    if (displayFormat === SectionColumnConfigDataBadgeMode.Badge) {
      return this.getBadgeModeAssetsForColumnType(columnType);
    }
    return this.getPlainTextListItemsForColumnType(columnType, displayFormat, undefined);
  }

  private getBadgeModeAssetsForColumnType(columnType: SectionColumnType): BadgeAsset[] {
    const uniqueBadges: VariantBadge[] = [];
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    variants.forEach(variant => {
      const variantListValues = this.getPlainTextListItemsForColumnType(
        columnType,
        SectionColumnConfigDataBadgeMode.Badge,
        variant
      );
      variantListValues.forEach(variantListValue => {
        const variantBadge = this.getBadgeAssetForColumnType(columnType, variantListValue);
        if (variantBadge) uniqueBadges.push(variantBadge);
      });
    });
    return uniqueBadges
      .uniqueByProperty('id')
      .map(variantBadge => variantBadge.image ?? null)
      .filterNulls();
  }

  private getPlainTextListItemsForColumnType(
    columnType: SectionColumnType,
    displayFormat: SectionColumnConfigDataValue,
    variant?: Variant
  ): string[] {
    if (RegexUtils.productTagGroupRegex.test(columnType)) {
      return this.getProductTagNamesWithinTagGroup(
        this.companyConfig?.enabledCuratedTagGroupIds,
        this.companyConfig?.enabledCuratedTagIds,
        columnType,
        variant,
        displayFormat === SectionColumnConfigDataBadgeMode.Badge
      );
    }
    switch (columnType) {
      case SectionColumnCannabinoidType.ListCannabinoids:
        return this.getListCannabinoids(displayFormat, variant);
      case SectionColumnTerpeneType.ListTerpenes:
        return this.getListTerpenes(displayFormat, variant);
      case SectionColumnProductInfoType.Flavor:
        return this.getListFlavors(variant);
      case SectionColumnProductInfoType.Aroma:
        return this.getListAromas(variant);
      case SectionColumnProductInfoType.Appearance:
        return this.getListAppearances(variant);
      case SectionColumnProductInfoType.GrowingConditions:
        // Growing conditions are PascalCase enum values; badge mappings are keyed on the raw value
        return displayFormat === SectionColumnConfigDataBadgeMode.Badge
          ? this.getListGrowingConditions(variant)
          : this.getListGrowingConditions(variant).map(it => StringUtils.splitPascalCasePreserveAcronyms(it));
      case SectionColumnProductInfoType.CraftDesignation:
        return this.getListCraftDesignations(variant);
      case SectionColumnProductInfoType.Effects:
        return this.getListEffects(variant);
      case SectionColumnProductInfoType.HelpsWith:
        return this.getListHelpsWith(variant);
      default:
        return [];
    }
  }

  /**
   * The original thc/cbd cannabinoid logic displays numbers less than 1 as "<1", so
   * displayEntireNumberIfItIsLessThanOne was added to keep this old logic for thc/cbd,
   * but for all the new "secondary" cannabinoids and terpenes, they always display as the full number.
   */
  @memoize
  protected getFixedPointRangeStringNoUnits(
    minVal: number,
    maxVal: number,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint: number | undefined | null,
    displayEntireNumberIfItIsLessThanOne = true
  ): string {
    if (displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon) {
      return minVal > 0 || maxVal > 0 ? '✔' : '-';
    }
    if (minVal === -1 || maxVal === -1) return '';
    if (minVal === maxVal) {
      if (!displayEntireNumberIfItIsLessThanOne && minVal < 1) {
        return String('<1').trim();
      }
      // ie, don't return 14-14, just return 14,
      return fixedPoint ? minVal.toFixed(fixedPoint) : `${minVal}`;
    }
    return fixedPoint ? minVal.toFixed(fixedPoint) + ' - ' + maxVal.toFixed(fixedPoint) : `${minVal} - ${maxVal}`;
  }

  @memoize
  getMinRowNumericSize(): number {
    return Math.min(...this.getScopedVisibleVariantsElseRowVariants().map(v => v.getNumericSize()));
  }

  @memoize
  getMaxRowNumericSize(): number {
    return Math.max(...this.getScopedVisibleVariantsElseRowVariants().map(v => v.getNumericSize()));
  }

  protected getEmptyStateForCannabinoid(variants: Variant[]): string | null {
    const firstVariant = variants?.firstOrNull();
    const displayCannabinoid = firstVariant?.shouldDisplayCannabinoidValue();
    const cuomIsNA = firstVariant?.cannabisUnitOfMeasure === CannabisUnitOfMeasure.NA;
    switch (true) {
      case !firstVariant:
        return '';
      case !displayCannabinoid:
        return '-';
      case cuomIsNA:
        return '--';
    }
    return null;
  }

  /**
   * isMarkedPresent covers a compound set by hand as present or top, which counts as present without a value.
   */
  protected hasPresentValueElseEmptyState(value: string, isMarkedPresent: boolean = false): string {
    if (isMarkedPresent) return '✔';
    switch (value) {
      case undefined:
      case null:
      case '':
      case '-':
      case '--':
      case '0':
        return '-';
      default:
        return '✔';
    }
  }

  /**
   * getTAC is called in here because there are a lot of places in the app that override getCannabinoid to remove
   * spaces and other characters. Therefore, this prevents searching through the code base for all the getCannabinoid
   * overrides and implementing the same logic for getTAC.
   */
  @memoize
  public getCannabinoid(
    cannabinoid: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    if (cannabinoid === 'TAC') return this.getTotalActiveCannabinoids();
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    const emptyState = this.getEmptyStateForCannabinoid(variants);
    if (emptyState !== null) return emptyState;
    const unitOfMeasureString = variants?.firstOrNull()?.getCannabinoidUnitOfMeasureString(this.menu);
    const cannabinoidValueAsString = this.displayCannabinoidInRanges()
      ? this.getCannabinoidRowRangeAsString(cannabinoid, unitOfMeasureString, displayFormat)
      : this.getCannabinoidAsString(cannabinoid, unitOfMeasureString, displayFormat);
    if (displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon) {
      return this.hasPresentValueElseEmptyState(cannabinoidValueAsString);
    }
    return cannabinoidValueAsString;
  }

  @memoize
  protected getCannabinoidRowRangeAsString(
    cannabinoid: string,
    unitOfMeasureString: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    const range = this.getRowRangeCannabinoid(cannabinoid, displayFormat, fixedPoint, variant);
    switch (true) {
      case displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon:
        return range;
      case !range:
        return `-`;
      default:
        return `${range}${this.getCannabinoidFormatedUnitOfMeasureString(unitOfMeasureString)}`.trim();
    }
  }

  /**
   * Accounts for spacing in front of the unit of measure.
   */
  protected getCannabinoidFormatedUnitOfMeasureString(unitOfMeasure: string): string {
    return this.showCUOMInHeader() ? '' : ` ${unitOfMeasure}`.replace(' %', '%');
  }

  /**
   * Accounts for spacing in front of the unit of measure.
   */
  protected getTerpeneFormatedUnitOfMeasureString(unitOfMeasure: string): string {
    return this.showTUOMInHeader() ? '' : ` ${unitOfMeasure}`.replace(' %', '%');
  }

  @memoize
  protected getCannabinoidAsString(
    cannabinoid: string,
    units: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    const parsedCannabinoid = this.getMinRowCannabinoid(cannabinoid, variant);
    const isListCannabinoid =
      displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.ListNumericValue;
    switch (true) {
      case displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon: {
        return parsedCannabinoid > 0 ? '✔' : '-';
      }
      case !Number.isFinite(parsedCannabinoid):
      case parsedCannabinoid < 0:
      case isListCannabinoid && parsedCannabinoid < CompoundUtils.smallestAllowableValue: {
        return '-';
      }
      case !isListCannabinoid &&
        parsedCannabinoid < 1 &&
        (cannabinoid === PrimaryCannabinoid.THC || cannabinoid === PrimaryCannabinoid.CBD): {
        return `<1${this.getCannabinoidFormatedUnitOfMeasureString(units)}`;
      }
      case Number.isFinite(fixedPoint): {
        const fixed = parsedCannabinoid.toFixed(fixedPoint);
        return `${fixed}${this.getCannabinoidFormatedUnitOfMeasureString(units)}`.trim();
      }
      default: {
        const rounded = Math.round((parsedCannabinoid + Number.EPSILON) * 100) / 100;
        return `${rounded}${this.getCannabinoidFormatedUnitOfMeasureString(units)}`.trim();
      }
    }
  }

  /**
   * If you want to shape cannabinoids and Total Active Cannabinoids the same way, then you only need to override
   * getCannabinoid, because getCannabinoid calls getTotalActiveCannabinoids internally, and is used to display
   * getTotalActiveCannabinoids in the UI.
   *
   * For most cases, use getCannabinoid('TAC') instead of getTotalActiveCannabinoids, due to getCannabinoid being
   * overridden in many places to change the output formatting of cannabinoids.
   */
  @memoize
  getTotalActiveCannabinoids(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    const emptyState = this.getEmptyStateForCannabinoid(variants);
    if (emptyState !== null) return emptyState;
    const enabledSecondaryCannabinoids = this.companyConfig?.enabledSecondaryCannabinoids || [];
    const unitOfMeasureString = variants?.firstOrNull()?.getCannabinoidUnitOfMeasureString(this.menu);
    let val: string = '';
    let totalActiveCannabinoids = variants
      ?.flatMap(v => {
        return v.useCannabinoidRange
          ? [
              v.getNumericMinTotalActiveCannabinoids(enabledSecondaryCannabinoids),
              v.getNumericMaxTotalActiveCannabinoids(enabledSecondaryCannabinoids)
            ]
          : [v.getNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids)];
      })
      ?.filter(tat => Number.isFinite(tat))
      ?.map(tat => NumberUtils.roundToTwoDecimalPlaces(tat))
      ?.unique(false)
      ?.sort(SortUtils.numberAscending);
    // correct for singular 0 value, so that function returns '-' when there are no cannabinoids
    if (totalActiveCannabinoids?.length === 1 && totalActiveCannabinoids?.firstOrNull() === 0) {
      totalActiveCannabinoids = [];
    }
    if (totalActiveCannabinoids?.length >= 2) {
      val = `${totalActiveCannabinoids?.firstOrNull()} - ${totalActiveCannabinoids?.last()}`;
    } else if (totalActiveCannabinoids?.length === 1) {
      val = `${totalActiveCannabinoids?.firstOrNull()}`;
    }
    if (!val) return `-`;
    return `${val}${this.getCannabinoidFormatedUnitOfMeasureString(unitOfMeasureString)}`.trim();
  }

  protected getEmptyStateForTerpene(variants: Variant[]): string | null {
    const firstVariant = variants?.firstOrNull();
    const displayTerpene = firstVariant?.shouldDisplayTerpeneValue();
    const tuomIsNA = firstVariant?.terpeneUnitOfMeasure === TerpeneUnitOfMeasure.NA;
    switch (true) {
      case !firstVariant:
        return '';
      case !displayTerpene:
        return '-';
      case tuomIsNA:
        return '--';
    }
    return null;
  }

  @memoize
  getTerpene(
    terpenePascalCased: string,
    displayFormat: SectionColumnConfigDataValue = SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue
  ): string {
    if (terpenePascalCased === SectionColumnConfigCollectiveTerpeneKey.TotalTerpene) {
      return this.getTotalActiveTerpenes();
    }
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    const emptyState = this.getEmptyStateForTerpene(variants);
    if (emptyState !== null) return emptyState;
    const unitOfMeasureString = variants?.firstOrNull()?.getTerpeneUnitOfMeasureString(this.menu);
    const terpeneValueAsString = this.displayTerpeneInRanges()
      ? this.getTerpeneRowRangeAsString(terpenePascalCased, unitOfMeasureString, displayFormat)
      : this.getTerpeneAsString(terpenePascalCased, unitOfMeasureString, displayFormat);
    if (displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon) {
      const isMarkedPresent = this.isTerpeneInPresentTerpenes(terpenePascalCased);
      return this.hasPresentValueElseEmptyState(terpeneValueAsString, isMarkedPresent);
    }
    return terpeneValueAsString;
  }

  /**
   * Present terpenes already include the top terpene.
   */
  @memoize
  protected isTerpeneInPresentTerpenes(terpenePascalCased: string): boolean {
    return this.getPresentTerpenes()?.some(terpene => StringUtils.toPascalCase(terpene) === terpenePascalCased);
  }

  @memoize
  protected getTerpeneRowRangeAsString(
    terpenePascalCased: string,
    unitOfMeasureString: string,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint?: number,
    variant?: Variant
  ): string {
    const terpeneCamelCased = StringUtils.pascalCaseToCamelCase(terpenePascalCased);
    const range = this.getRowRangeTerpene(terpeneCamelCased, displayFormat, fixedPoint, variant);
    switch (true) {
      case displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon:
        return range;
      case !range:
        return `-`;
      default:
        return `${range}${this.getTerpeneFormatedUnitOfMeasureString(unitOfMeasureString)}`.trim();
    }
  }

  @memoize
  protected getTerpeneAsString(
    terpenePascalCased: string,
    units: string,
    displayFormat: SectionColumnConfigDataValue,
    fixedPoint?: number,
    isListTerpene?: boolean,
    variant?: Variant
  ): string {
    const terpeneCamelCased = StringUtils.pascalCaseToCamelCase(terpenePascalCased);
    const parsedTerpene = this.getMinRowTerpene(terpeneCamelCased, variant);
    switch (true) {
      case displayFormat === SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.PresenceIcon: {
        return parsedTerpene > 0 ? '✔' : '-';
      }
      case !Number.isFinite(parsedTerpene):
      case parsedTerpene <= 0: {
        return '-';
      }
      case isListTerpene && parsedTerpene < 0.5: {
        return '-';
      }
      case Number.isFinite(fixedPoint): {
        const fixed = parsedTerpene.toFixed(fixedPoint);
        return `${fixed}${this.getTerpeneFormatedUnitOfMeasureString(units)}`.trim();
      }
      default: {
        const rounded = Math.round((parsedTerpene + Number.EPSILON) * 100) / 100;
        return `${rounded}${this.getTerpeneFormatedUnitOfMeasureString(units)}`.trim();
      }
    }
  }

  /**
   * If you want to shape terpenes and Total Active Terpenes the same way, then you only need to override getTerpene,
   * because getTerpene calls getTotalActiveTerpenes internally, and is used to display getTotalActiveTerpenes in the
   * UI.
   *
   * For most cases, use getTerpene('TotalTerpene') instead of getTotalActiveTerpenes, due to getTerpene being
   * overridden in many places to change the output formatting of terpenes.
   */
  @memoize
  public getTotalActiveTerpenes(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    const emptyState = this.getEmptyStateForTerpene(variants);
    if (emptyState !== null) return emptyState;
    const enabledTerpenesPascalCased = this.companyConfig?.enabledTerpenes || [];
    const unitOfMeasureString = variants?.firstOrNull()?.getTerpeneUnitOfMeasureString(this.menu);
    let val: string = '';
    let totalTerpenes = variants
      ?.flatMap(v => {
        return v.useTerpeneRange
          ? [
              v.getNumericMinTotalActiveTerpenes(enabledTerpenesPascalCased),
              v.getNumericMaxTotalActiveTerpenes(enabledTerpenesPascalCased)
            ]
          : [v.getNumericTotalActiveTerpenes(enabledTerpenesPascalCased)];
      })
      ?.filter(tat => Number.isFinite(tat))
      ?.map(tat => NumberUtils.roundToTwoDecimalPlaces(tat))
      ?.unique(false)
      ?.sort(SortUtils.numberAscending);
    // correct for singular 0 value, so that function returns '-' when there are no terpenes
    if (totalTerpenes?.length === 1 && totalTerpenes?.firstOrNull() === 0) {
      totalTerpenes = [];
    }
    if (totalTerpenes?.length >= 2) {
      val = `${totalTerpenes?.firstOrNull()} - ${totalTerpenes?.last()}`;
    } else if (totalTerpenes?.length === 1) {
      val = `${totalTerpenes?.firstOrNull()}`;
    }
    if (!val) return `-`;
    return `${val}${this.getTerpeneFormatedUnitOfMeasureString(unitOfMeasureString)}`.trim();
  }

  /**
   * @deprecated use getCannabinoid instead
   */
  @memoize
  public getThc(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    if (variants.length > 0) {
      const shouldDisplayTHC = variants?.firstOrNull()?.shouldDisplayCannabinoidValue();
      if (shouldDisplayTHC) {
        if (variants?.firstOrNull().cannabisUnitOfMeasure === CannabisUnitOfMeasure.NA) {
          return '--';
        }
        const unitOfMeasureString = variants?.firstOrNull()?.getCannabinoidUnitOfMeasureString(this.menu);
        // Get THC value
        if (this.displayCannabinoidInRanges()) {
          return this.getThcRowRangeAsString(unitOfMeasureString);
        } else {
          return this.getThcAsString(unitOfMeasureString);
        }
      } else {
        return '-';
      }
    }
    return '';
  }

  /**
   * @deprecated use getCannabinoidRowRangeAsString instead
   */
  @memoize
  protected getThcRowRangeAsString(unitOfMeasureString: string, fixedPoint?: number, variant?: Variant): string {
    const thcRange = this.getRowRangeCannabinoid(
      'THC',
      SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
      fixedPoint,
      variant
    );
    if (thcRange === '') {
      return `${CannabisUnitOfMeasure.NA}`;
    } else {
      return `${thcRange} ${this.showCUOMInHeader() ? '' : unitOfMeasureString}`.trim();
    }
  }

  /**
   * @deprecated use getCannabinoidAsString instead
   */
  @memoize
  protected getThcAsString(units: string, fixedPoint?: number, variant?: Variant): string {
    const parsedThc = this.getMinRowCannabinoid('THC', variant);
    if (parsedThc < 1) {
      return (this.showCUOMInHeader() ? `<1` : `<1 ${units}`).trim();
    } else {
      if (fixedPoint) {
        return `${parsedThc.toFixed(fixedPoint)} ${this.showCUOMInHeader() ? '' : units}`.trim();
      } else {
        const roundedThc =
          `${Math.round((parsedThc + Number.EPSILON) * 100) / 100} ` + `${this.showCUOMInHeader() ? '' : units}`;
        return roundedThc.trim();
      }
    }
  }

  /**
   * @deprecated use getCannabinoid instead
   */
  @memoize
  public getCbd(): string {
    const variants = this.getScopedVisibleVariantsElseRowVariants();
    if (variants.length > 0) {
      const shouldDisplayCBD = variants?.firstOrNull()?.shouldDisplayCannabinoidValue();
      if (shouldDisplayCBD) {
        if (variants?.firstOrNull().cannabisUnitOfMeasure === CannabisUnitOfMeasure.NA) {
          return '--';
        }
        const unitOfMeasureString = variants?.firstOrNull()?.getCannabinoidUnitOfMeasureString(this.menu);
        // Get CBD Value
        if (this.displayCannabinoidInRanges()) {
          return this.getCbdRowRangeAsString(unitOfMeasureString);
        } else {
          return this.getCbdAsString(unitOfMeasureString);
        }
      } else {
        return '-';
      }
    }
    return '';
  }

  /**
   * @deprecated use getCannabinoidRowRangeAsString instead
   */
  @memoize
  protected getCbdRowRangeAsString(unitOfMeasureString: string, fixedPoint?: number, variant?: Variant): string {
    const cbdRange = this.getRowRangeCannabinoid(
      'CBD',
      SectionColumnConfigDataForCannabinoidsAndTerpenesColumn.NumericValue,
      fixedPoint,
      variant
    );
    if (cbdRange === '') {
      return `${CannabisUnitOfMeasure.NA}`;
    } else {
      return `${cbdRange} ${this.showCUOMInHeader() ? '' : unitOfMeasureString}`.trim();
    }
  }

  /**
   * @deprecated use getCannabinoidAsString instead
   */
  @memoize
  protected getCbdAsString(units: string, fixedPoint?: number, variant?: Variant): string {
    const parsedCbd = this.getMinRowCannabinoid('CBD', variant);
    if (parsedCbd < 1) {
      return (this.showCUOMInHeader() ? `<1` : `<1 ${units}`).trim();
    } else {
      if (fixedPoint) {
        return `${parsedCbd.toFixed(fixedPoint)} ${this.showCUOMInHeader() ? '' : units}`.trim();
      } else {
        const roundedCbd =
          `${Math.round((parsedCbd + Number.EPSILON) * 100) / 100} ` + `${this.showCUOMInHeader() ? '' : units}`;
        return roundedCbd.trim();
      }
    }
  }

  @memoize
  public getCUOM(): string {
    return this.rowVariants?.firstOrNull()?.getCannabinoidUnitOfMeasureString(this.menu);
  }

  @memoize
  public getRowUniqueCUOM(): CannabisUnitOfMeasure {
    return this.rowVariants?.map(v => v.cannabisUnitOfMeasure)?.uniqueInstance(true) ?? CannabisUnitOfMeasure.UNKNOWN;
  }

  @memoize
  public getRowUniqueTUOM(): TerpeneUnitOfMeasure {
    return this.rowVariants?.map(v => v.terpeneUnitOfMeasure)?.uniqueInstance(true) ?? TerpeneUnitOfMeasure.UNKNOWN;
  }

  @memoize
  public getSectionUniqueCUOM(): CannabisUnitOfMeasure {
    const sectionCUOMs = this.section
      ?.getScopedVisibleVariants(this.menu, this.locationConfig?.priceFormat, this.menu?.isSectionLevelOverflow())
      ?.map(v => v.cannabisUnitOfMeasure);
    return sectionCUOMs.uniqueInstance(true) ?? CannabisUnitOfMeasure.UNKNOWN;
  }

  @memoize
  public getSectionUniqueTUOM(): TerpeneUnitOfMeasure {
    const sectionTUOMs = this.section
      ?.getScopedVisibleVariants(this.menu, this.locationConfig?.priceFormat, this.menu?.isSectionLevelOverflow())
      ?.map(v => v.terpeneUnitOfMeasure);
    return sectionTUOMs.uniqueInstance(true) ?? TerpeneUnitOfMeasure.UNKNOWN;
  }

  @memoize
  displayCannabinoidInRanges(): boolean {
    const companyUsesCannabinoidRange = this.companyConfig?.cannabinoidDisplayType === CannabinoidDisplayType.Range;
    const someRowVariantsUseCannabinoidRange = () => {
      return this.getScopedVisibleVariantsElseRowVariants()?.some(v => v.useCannabinoidRange);
    };
    return companyUsesCannabinoidRange || someRowVariantsUseCannabinoidRange();
  }

  @memoize
  displayTerpeneInRanges(): boolean {
    const companyUsesTerpeneRange = this.companyConfig?.terpeneDisplayType === TerpeneDisplayType.Range;
    const someRowVariantsUseTerpeneRange = () => {
      return this.getScopedVisibleVariantsElseRowVariants()?.some(v => v?.useTerpeneRange);
    };
    return companyUsesTerpeneRange || someRowVariantsUseTerpeneRange();
  }

  @memoize
  public showCUOMInHeader(): boolean {
    const uniqueCUOM = this.getSectionUniqueCUOM();
    return this.menu?.menuOptions?.showCUOMInHeader && exists(uniqueCUOM);
  }

  @memoize
  public showTUOMInHeader(): boolean {
    const uniqueTUOM = this.getSectionUniqueTUOM();
    // showTUOMInHeader doesn't exist, use showCUOMInHeader instead
    return this.menu?.menuOptions?.showCUOMInHeader && exists(uniqueTUOM);
  }

  @memoize
  public getQuantityInStock(): number {
    return this.rowVariants?.firstOrNull()?.inventory?.quantityInStock ?? 0;
  }

  @memoize
  public getQuantityAndSizeString(): string {
    const productType = this.rowProductType();
    const isEdible = productType === ProductType.Edible;
    const isSeed = productType === ProductType.Seed;
    const isReadyToDrinkBeverage = VariantTypeUtils.isReadyToDrinkBeverageType(this.rowVariantType());
    switch (true) {
      case isEdible && !isReadyToDrinkBeverage:
        return this.getQuantityAndSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages();
      case isSeed:
        return this.getQuantityAndSizeHelperForSeeds();
      default:
        const quantity = this.getQuantityString();
        const size = this.getSize();
        return !size || size === '-' || size === '--' ? size : [quantity, size].filter(s => exists(s)).join(' x ');
    }
  }

  @memoize
  protected getQuantityAndSizeHelperForSeeds(): string | null {
    const packageQuantity = this.getMinPackageQuantity();
    return `${packageQuantity} ${this.seedPackageQuantityWord()}`;
  }

  @memoize
  protected getQuantityAndSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages(): string | null {
    const packageQuantity = this.getMinPackageQuantity();
    return `${packageQuantity} ${this.edibleNonReadyToDrinkPackageQuantityWord()}`;
  }

  protected edibleNonReadyToDrinkPackageQuantityWord(variant?: Variant): string {
    return 'Pack';
  }

  protected seedPackageQuantityWord(variant?: Variant): string {
    return 'Pack';
  }

  @memoize
  public getQuantityString(variant?: Variant): string {
    return this.getQuantity(variant).toString();
  }

  @memoize
  public getQuantity(variant?: Variant): number {
    if (exists(variant)) {
      return variant?.packagedQuantity;
    } else if (this.variantLineItemMode) {
      return this.rowVariants?.firstOrNull()?.packagedQuantity;
    } else {
      return 1;
    }
  }

  @memoize
  public getGridQuantity(variant?: Variant) {
    if (exists(variant)) {
      return variant?.packagedQuantity;
    } else if (this.variantLineItemMode) {
      return this.rowVariants?.firstOrNull()?.packagedQuantity;
    } else {
      return 1;
    }
  }

  @memoize
  public getMinPackageQuantity(): number {
    const packageQuantity = this.rowVariants?.map(v => v?.packagedQuantity) || [];
    const min = Math.min(...packageQuantity);
    return Number.isFinite(min) ? min : 0;
  }

  @memoize
  public getMaxPackageQuantity(): number {
    const packageQuantity = this.rowVariants?.map(v => v?.packagedQuantity) || [];
    return Math.max(...packageQuantity, 0);
  }

  /**
   * Returns the minimum unit size of the variants in the group.
   * Can return Infinity if unit sizes are not specified.
   */
  @memoize
  public getMinNumericUnitSize(): number {
    const size = this.rowVariants?.map(v => v?.unitSize) || [];
    return Math.min(...size);
  }

  @memoize
  public getMaxNumericUnitSize(): number {
    const size = this.rowVariants?.map(v => v?.unitSize) || [];
    return Math.max(...size, 0);
  }

  @memoize
  public getSize(variant?: Variant): string {
    if (exists(variant)) {
      return variant?.getFormattedUnitSize();
    } else if (this.variantLineItemMode) {
      return this.rowVariants?.firstOrNull()?.getFormattedUnitSize();
    } else {
      return '';
    }
  }

  @memoize
  public getGradingResultFor(gradingProvider: GradingProvider): string | null {
    if (!this.rowVariants?.length) return null;
    const gradingResult = this.rowVariants?.firstOrNull()?.getGradingResultGradeFor(gradingProvider);
    if (!Number.isFinite(gradingResult)) return null;
    if (gradingProvider === GradingProvider.Sensemillier) {
      return NumberUtils.removeTrailingZeros(NumberUtils.roundToTwoDecimalPlaces(gradingResult).toString());
    }
    return gradingResult.toString();
  }

  @memoize
  public hasBadges(): boolean {
    return this.getAllVariantBadges()?.length > 0;
  }

  @memoize
  public hasBrands(): boolean {
    return exists(this.getBrand());
  }

  @memoize
  public getBadgesFor(variant: Variant): VariantBadge[] {
    return BadgeUtils.getVariantVisibleBadges(this.menu, this.section, [variant]);
  }

  @memoize
  public getProductTagsFor(variant: Variant): ProductTag[] | null {
    const productTags = this.section?.getAllProductTags();
    const tagsIds = variant?.getAllProductTagIds();
    return (
      productTags?.filter(tag => {
        return (
          tagsIds?.includes(tag.id) &&
          tag?.isVisibleTag(this.companyConfig?.enabledCuratedTagGroupIds, this.companyConfig?.enabledCuratedTagIds)
        );
      }) || null
    );
  }

  @memoize
  public getAllVariantBadges(): VariantBadge[] {
    return BadgeUtils.getVariantVisibleBadges(this.menu, this.section, this.getScopedVisibleVariants());
  }

  @memoize
  public getBadgesInlineWithName(): VariantBadge[] {
    return this.getAllVariantBadges();
  }

  @memoize
  public getBadgesForBadgeColumn(): VariantBadge[] {
    return this.getAllVariantBadges();
  }

  /**
   * Row methods are calculated based on the variants associated with the line item.
   */
  @memoize
  rowBrand(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.brand));
  }

  /**
   * Row methods are calculated based on the variants associated with the line item.
   */
  @memoize
  rowManufacturer(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.manufacturer));
  }

  @memoize
  rowStrain(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.strain));
  }

  /**
   * Row methods are calculated based on the variants associated with the line item.
   */
  @memoize
  rowStrainType(): StrainType | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.classification));
  }

  @memoize
  rowLineage(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getLineagesAsString()));
  }

  /**
   * Row methods are calculated based on the variants associated with the line item.
   */
  @memoize
  rowProductType(): ProductType | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.productType));
  }

  /**
   * Row methods are calculated based on the variants associated with the line item.
   */
  @memoize
  rowVariantType(): VariantType | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.variantType));
  }

  @memoize
  rowEffects(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getEffectsAsString()));
  }

  @memoize
  rowAromas(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getAromasAsString()));
  }

  @memoize
  rowFlavorGroup(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.flavorGroup));
  }

  @memoize
  rowFlavors(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getFlavorsAsString()));
  }

  @memoize
  rowHelpsWith(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getHelpsWithAsString()));
  }

  @memoize
  rowAppearances(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getAppearancesAsString()));
  }

  @memoize
  rowGrowingConditions(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getGrowingConditionsAsString()));
  }

  @memoize
  getMinEnergyScore(): number | null {
    const scores =
      this.rowVariants
        ?.map(v => v?.getEnergyScoreAsNumber())
        .filter((score): score is number => Number.isFinite(score as number)) ?? [];
    if (scores.length === 0) {
      return null;
    }
    return Math.min(...scores);
  }

  @memoize
  getMaxEnergyScore(): number | null {
    const scores =
      this.rowVariants
        ?.map(v => v?.getEnergyScoreAsNumber())
        .filter((score): score is number => Number.isFinite(score as number)) ?? [];
    if (scores.length === 0) {
      return null;
    }
    return Math.max(...scores);
  }

  @memoize
  rowCraftDesignations(): string | null {
    return StringUtils.getStringModeOrNull(this.rowVariants?.map(v => v?.getCraftDesignationsAsString()));
  }

  getHideSale(): boolean {
    return this.menu?.menuOptions?.hideSale;
  }

  hideOverflowItem(): boolean {
    return this.itemOverflowStatus === SectionItemOverflowStatus.Overflow;
  }

  @memoize
  isAccessory(): boolean {
    return this.rowVariants?.some(variant => variant?.productType === ProductType.Accessories);
  }

  isVariantAnAccessory(variant: Variant): boolean {
    return variant?.productType === ProductType.Accessories;
  }

  @memoize
  public isNonCannabinoidOtherVariant(): boolean {
    return (
      this.rowProductType() === ProductType.Other &&
      !VariantTypeUtils.isOtherTypeWithCannabinoids(this.rowVariantType())
    );
  }

  @memoize
  isVariantNonCannabinoidOtherVariant(variant: Variant): boolean {
    return (
      variant?.productType === ProductType.Other && !VariantTypeUtils.isOtherTypeWithCannabinoids(variant?.variantType)
    );
  }

  @memoize
  public isNonCannabinoidVariant(): boolean {
    return this.isNonCannabinoidOtherVariant() || this.isAccessory();
  }

  @memoize
  public getProductFlavors(): string[] {
    return (
      this.getScopedVisibleVariantsElseRowVariants()
        ?.flatMap(v => v?.getFlavors())
        ?.filterNulls()
        ?.unique() ?? []
    );
  }

  @memoize
  public inStockRowVariants(): Variant[] {
    return this.variantLineItemMode ? this.rowVariants : this.rowVariants?.filter(v => v.inventory.inStock());
  }

  public getVariantMatch(columnVM: SectionColumnViewModel): Variant {
    return this.getVariantFromGridColumn(columnVM);
  }

  @memoize
  getVariantDescriptionField(): string {
    return this.getScopedVisibleVariants()
      ?.map(v => v?.description)
      ?.filterNulls()
      ?.firstOrNull();
  }

  @memoize
  public showFullDescription(): boolean {
    return this.menu?.menuOptions?.showFullDescription ?? false;
  }

  @memoize
  getProductDesc(): string {
    if (this.menu?.menuOptions?.hideDescription) {
      return null;
    } else if (this.menu?.menuOptions?.showFullDescription) {
      return this.getScopedVisibleVariants()
        ?.map(v => v?.description)
        ?.filterNulls()
        ?.firstOrNull();
    } else {
      return (
        this.getScopedVisibleVariants()
          ?.map(v => v?.shortDescription)
          ?.filterNulls()
          ?.firstOrNull() ||
        this.getScopedVisibleVariants()
          ?.map(v => v?.description)
          ?.filterNulls()
          ?.firstOrNull()
      );
    }
  }

  @memoize
  getRichTextDescription(): string {
    if (this.menu?.menuOptions?.hideDescription || !this.menu?.menuOptions?.showFullDescription) {
      // If the description is hidden, or not set to use the full description, return null
      return null;
    } else {
      return this.getScopedVisibleVariants()
        ?.map(v => v?.richTextDescription)
        ?.filterNulls()
        ?.firstOrNull();
    }
  }

  @memoize
  getTopFlavorPlainText(): string | undefined {
    return this.rowVariants?.firstOrNull()?.flavorGroup ?? '';
  }

  @memoize
  getListFlavors(variant?: Variant): string[] {
    const flavors = exists(variant)
      ? (variant.flavors.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.flavors)
          ?.filterNulls() ?? []);
    return [...new Set(flavors)];
  }

  @memoize
  getListAromas(variant?: Variant): string[] {
    const aromas = exists(variant)
      ? (variant.aromas.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.aromas)
          ?.filterNulls() ?? []);
    return [...new Set(aromas)];
  }

  @memoize
  getListAppearances(variant?: Variant): string[] {
    const appearances = exists(variant)
      ? (variant.appearances.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.appearances)
          ?.filterNulls() ?? []);
    return [...new Set(appearances)];
  }

  @memoize
  getListGrowingConditions(variant?: Variant): string[] {
    const conditions = exists(variant)
      ? (variant.growingConditions.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.growingConditions)
          ?.filterNulls() ?? []);
    return [...new Set(conditions)];
  }

  @memoize
  getListCraftDesignations(variant?: Variant): string[] {
    const designations = exists(variant)
      ? (variant.craftDesignations.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.craftDesignations)
          ?.filterNulls() ?? []);
    return [...new Set(designations)];
  }

  @memoize
  getListEffects(variant?: Variant): string[] {
    const effects = exists(variant)
      ? (variant.effects.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.effects)
          ?.filterNulls() ?? []);
    return [...new Set(effects)];
  }

  @memoize
  getListHelpsWith(variant?: Variant): string[] {
    const helpsWith = exists(variant)
      ? (variant.helpsWith.filterNulls() ?? [])
      : (this.getScopedVisibleVariantsElseRowVariants()
          ?.flatMap(variant => variant?.helpsWith)
          ?.filterNulls() ?? []);
    return [...new Set(helpsWith)];
  }

  @memoize
  getEnergyScore(): number | null {
    return (
      this.getScopedVisibleVariants()
        ?.map(variant => variant?.getEnergyScoreAsNumber())
        ?.filter(score => Number.isFinite(score))
        ?.mode() ?? null
    );
  }

  /**
   * Is the variant scoped to the currently selected grid columns and is the variant price visible and greater than 0?
   * Then the variant is scoped and visible, so add it to the list of returned variants.
   */
  @memoize
  getScopedVisibleVariants(): Variant[] {
    if (this.section?.isCustomLayoutMode() && this.menu.theme === PrintCardThemeId.GlassHouseBrandGroup) {
      return this.rowVariants;
    } else if (this.variantLineItemMode || this.section?.isChildVariantListMode()) {
      return this.rowVariants;
    } else {
      const themeId = this.menu?.theme;
      const locationId = this.menu?.locationId;
      const companyId = this.menu?.companyId;
      const priceStream = this.locationConfig?.priceFormat;
      const hideSale = this.getHideSale();
      return this.rowVariants?.filter(variant => {
        const isWithinGridScope = this.section?.isVariantWithinGridScope(variant, this.section?.layoutType, locationId);
        const hidePrice = exists(this.hidePriceOnVariantIds?.find(id => id === variant?.id));
        let price: number;
        // always show out of stock products for print card menus
        const showZeroStockItems = MenuUtils.isPrintCardMenu(this.menu) || this.section?.showZeroStockItems;
        if (showZeroStockItems || variant?.inventory?.inStock()) {
          price = variant?.getVisiblePrice(themeId, locationId, companyId, priceStream, hideSale);
        }
        return !isWithinGridScope || hidePrice ? false : price > 0;
      });
    }
  }

  /**
   * Are there any scoped & visible variants? Then return them, else return this.rowVariants.
   */
  @memoize
  getScopedVisibleVariantsElseRowVariants(): Variant[] {
    const visibleAndHavePrice = this.getScopedVisibleVariants();
    return visibleAndHavePrice?.length > 0 ? visibleAndHavePrice : this.rowVariants;
  }

  getLayoutType(): SectionLayoutType {
    return this.section?.layoutType;
  }

  incrementUniqueIdOffset(): void {
    this.uniqueIdOffset++;
  }

  uniqueId(): string {
    return this.rowVariants
      ?.map(v => v.id)
      ?.sort()
      ?.join(',');
  }

  /**
   * This is needed for section level snap transitions.
   *
   * Problem: when rowCount === rowViewModels?.length + 1, trackBy fails and destroys all line items added to the DOM,
   * and then recreates them, which causes the snap transition to fail.
   *
   * Solution: to combat this, we add a unique id offset to the uniqueId, which we call uniqueIdWithTransitionOffset.
   * This offset increments by 1 right before the rowViewModel is about to be snapped into view.
   * This stops the trackBy decorator from destroying/recreating the line items added to the DOM.
   */
  uniqueIdWithTransitionOffset(): string {
    return this.uniqueId() + this.uniqueIdOffset;
  }

  @memoize
  getVariantFromGridColumn(columnVM: SectionColumnViewModel): Variant {
    return this.rowVariants?.find(v => {
      const layoutType = columnVM?.sectionLayoutType;
      if (!this.section?.enabledVariantIds?.find(i => i === v?.id)) {
        return false;
      } else if (layoutType === SectionLayoutType.List || layoutType === SectionLayoutType.ChildVariantList) {
        return true;
      } else {
        return v?.getGridNames(layoutType, this.locationConfig?.locationId)?.contains(columnVM.columnTitle);
      }
    });
  }

  @memoize
  private getBadgeMappingForColumnType(columnType: SectionColumnType): BadgeMapping | undefined {
    return this.menu?.badgeMappings?.find(mapping => mapping.categoryKey === columnType.valueOf());
  }

  @memoize
  private getVariantBadgeForColumnTypeAndProductValue(
    columnType: SectionColumnType,
    productValue: string
  ): VariantBadge | undefined {
    const badgeMapping = this.getBadgeMappingForColumnType(columnType);
    const restrictedValues = badgeMapping.restrictedValues;
    if (restrictedValues.length && !restrictedValues.includes(productValue)) return undefined;
    const customMappingBadge = badgeMapping?.hydratedCustomMappings?.get(productValue);
    return customMappingBadge ?? badgeMapping?.hydratedCuratedMappings?.get(productValue);
  }

  @memoize
  private getBadgeMappingKeyForColumnType(columnType: SectionColumnType, forcedKey?: string): string | undefined {
    if (RegexUtils.productTagGroupRegex.test(columnType)) return forcedKey ?? '';
    switch (columnType) {
      case SectionColumnProductInfoType.StrainType:
        return this.getStrainType().valueOf();
      case SectionColumnProductInfoType.VariantType:
        return this.rowVariantType().valueOf();
      case SectionColumnTerpeneType.TopTerpene:
        return this.getTopTerpene();
      case SectionColumnCannabinoidType.TopCannabinoid:
        return this.getTopCannabinoid();
      case SectionColumnTerpeneType.ListTerpenes:
      case SectionColumnCannabinoidType.ListCannabinoids:
      case SectionColumnProductInfoType.Flavor:
      case SectionColumnProductInfoType.Aroma:
      case SectionColumnProductInfoType.Appearance:
      case SectionColumnProductInfoType.GrowingConditions:
      case SectionColumnProductInfoType.CraftDesignation:
      case SectionColumnProductInfoType.Effects:
      case SectionColumnProductInfoType.HelpsWith:
        return forcedKey ? forcedKey : '';
      case SectionColumnProductInfoType.TopFlavor:
        return this.getTopFlavorPlainText();
      case SectionColumnProductInfoType.EnergyScore:
        const energyScore = this.getEnergyScore();
        if (energyScore) return `EnergyScore_${energyScore}`;
        return '';
      default:
        return undefined;
    }
  }

  @memoize
  private resolveColumnType(columnType: SectionColumnType): SectionColumnType {
    // Some column config keys are resolved to the same badge mapping category key as their list counterpart.
    // For example, the badge mappings for TopTerpene are contained in the badge mapping for ListTerpenes.
    switch (columnType) {
      case SectionColumnConfigCollectiveTerpeneKey.TopTerpene:
        return SectionColumnConfigCollectiveTerpeneKey.ListTerpenes;
      case SectionColumnConfigCollectiveCannabinoidKey.TopCannabinoid:
        return SectionColumnConfigCollectiveCannabinoidKey.ListCannabinoids;
      case SectionColumnProductInfoType.TopFlavor:
        return SectionColumnProductInfoType.Flavor;
      case SectionColumnProductInfoType.StrainType:
        return SectionColumnConfigProductInfoKey.StrainType;
      default:
        return columnType;
    }
  }

  @memoize
  getBadgeAssetForColumnType(columnType: SectionColumnType, forcedKey?: string): VariantBadge | undefined {
    const mappingKey = this.getBadgeMappingKeyForColumnType(columnType, forcedKey);
    if (mappingKey === '-' || mappingKey === '--' || mappingKey === '' || !mappingKey) return undefined;
    const resolvedColumnType = this.resolveColumnType(columnType);
    const asset: VariantBadge | undefined = this.getVariantBadgeForColumnTypeAndProductValue(
      resolvedColumnType,
      mappingKey
    );
    if (!environment.production && !asset) {
      console.warn(`badgeMapping within "${columnType}" column could not be resolved for value "${mappingKey}"`);
    }
    return asset;
  }

  /* ************************** Label Component Interface ************************** */

  getMenuForLabelComponent(): Menu {
    return this.menu;
  }

  getSectionForLabelComponent(): SectionWithProducts {
    return this.section;
  }

  getVariantsForLabelComponent(): Variant[] {
    return this.getScopedVisibleVariants();
  }

  getLocationConfigForLabelComponent(): LocationConfiguration {
    return this.locationConfig;
  }

  getCompanyConfigForLabelComponent(): CompanyConfiguration {
    return this.companyConfig;
  }

  getShutOffLabelForLabelComponent(): boolean {
    return !this.menu?.getShowInlineLabels();
  }

  getOverridePriceStreamForLabelComponent(): LocationPriceStream {
    return null;
  }

  getShutOffSaleLabelForLabelComponent(): boolean {
    return false;
  }

  /* ****************************************************************************** */
}
